import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  WebPreEnrollConfig,
  ConfigApi,
  Configuration,
} from '@clear-denver/tpc-openapi-typescript/build/v1/lookup'
import { v4 as uuid, validate as uuidValidate } from 'uuid'

export const mockConfig = {
  locations: {
    displayConversionCouponInfo: true,
  },
  prePay: {
    enabled: true,
  },
  youngAdultDiscount: {
    isPromoActive: true,
    showBanner: true,
    showModule: true,
  },
  layoutRefresh: {
    displayPersonalInformationAdvisoryHeader: false,
    displayPersonalInformationRevisedLayout: false,
    displayAuthenticationRevisedLayout: false,
    displayLocationSearchRevisedLayout: false,
  },
}

type ConfigValue = string | number | boolean
type DynamicConfigEntry = { [configGroup: string]: ConfigValue }
export type DynamicConfiguration = DynamicConfigEntry & WebPreEnrollConfig

const isConfig = (x: unknown): x is DynamicConfiguration => {
  return (
    typeof x === 'object' &&
    x !== null &&
    !Array.isArray(x) &&
    Object.keys(x).length > 0
  )
}

export type ConfigContextValues = {
  refetchConfig: () => void
  config: DynamicConfiguration
}

const initialState: ConfigContextValues = {
  refetchConfig: () => null,
  config: null as never,
}

export const ConfigContext = createContext<ConfigContextValues>(initialState)

export const ConfigProvider = ({
  children,
}: Readonly<{
  children: React.ReactNode
}>) => {
  const [config, setConfig] = useState<DynamicConfiguration>()

  const configClient = useMemo(
    () =>
      new ConfigApi(
        new Configuration({
          basePath: process.env.NEXT_PUBLIC_LOOKUP_API_ENDPOINT,
        })
      ),
    []
  )
  const fallbackDeviceId = useMemo(() => uuid(), [])
  const fetchConfig = useCallback(async () => {
    const deviceId = getDeviceId(fallbackDeviceId)
    const res = await configClient.getServiceConfigByNameV2Raw({
      service: 'web-pre-enroll-v2',
      clientId: deviceId,
    })
    await res.raw
      .json()
      .then((config: unknown) => {
        if (isConfig(config)) {
          setConfig(config)
        }
      })
      .catch(() => {
        /**
         * should always be valid JSON
         */
      })
  }, [configClient, fallbackDeviceId])

  useEffect(() => {
    fetchConfig()
  }, [fetchConfig])

  return (
    <ConfigContext.Provider
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      value={{ config: config!, refetchConfig: fetchConfig }}
    >
      {children}
    </ConfigContext.Provider>
  )
}

const getDeviceId = (fallbackDeviceId: string): string => {
  const DEVICE_ID_KEY = 'deviceId'
  try {
    let deviceId = localStorage.getItem(DEVICE_ID_KEY)
    if (!deviceId || !uuidValidate(deviceId)) {
      deviceId = uuid()
      localStorage.setItem(DEVICE_ID_KEY, deviceId)
    }
    return deviceId
  } catch (e) {
    return fallbackDeviceId
  }
}

export const useConfigContext = () => useContext(ConfigContext)
